// Perimeter smoothing helpers shared by the 3D viewer, seams, and SVG export.
//
// When a vertex is marked as "smooth", the two edges meeting at that vertex
// are replaced by a quadratic Bezier arc through the midpoints of those
// edges, with the vertex itself acting as the control point. Sharp vertices
// stay as-is. This keeps the polygon topology unchanged (no new vertices are
// introduced in the store) while giving a rounded corner in every context.

const midpoint = (a, b) => a.map((_, k) => (a[k] + b[k]) / 2);

const bez2 = (p0, p1, p2, t) => {
  const u = 1 - t;
  return p0.map((_, k) => u * u * p0[k] + 2 * u * t * p1[k] + t * t * p2[k]);
};

/**
 * Return a densified perimeter for one face.
 * @param {number[]} faceIdxs      Vertex indices for this face (in order).
 * @param {number[][]} vertices    Full vertex array (any dimension ≥ 2).
 * @param {Set<number>} smoothSet  Vertex indices flagged as smooth.
 * @param {number} samples         Samples per smoothed corner (default 8).
 * @returns {number[][]}           Ordered polyline of the perimeter.
 *   Note: if no vertex in this face is smooth, the returned array is exactly
 *   `faceIdxs.map(i => vertices[i])` (no allocation change downstream).
 */
export function smoothPerimeter(faceIdxs, vertices, smoothSet, samples = 8) {
  const n = faceIdxs.length;
  if (!smoothSet || smoothSet.size === 0) {
    return faceIdxs.map((i) => vertices[i]);
  }
  const anySmooth = faceIdxs.some((i) => smoothSet.has(i));
  if (!anySmooth) return faceIdxs.map((i) => vertices[i]);

  const V = faceIdxs.map((i) => vertices[i]);
  const out = [];
  for (let i = 0; i < n; i++) {
    const curr = V[i];
    const prev = V[(i - 1 + n) % n];
    const next = V[(i + 1) % n];
    if (smoothSet.has(faceIdxs[i])) {
      const mp = midpoint(prev, curr);
      const mn = midpoint(curr, next);
      for (let s = 0; s <= samples; s++) {
        out.push(bez2(mp, curr, mn, s / samples));
      }
    } else {
      out.push(curr);
    }
  }
  return out;
}
